import axiosInstance from '../common/axiosInstance';
import { useQuery, useMutation } from "@tanstack/react-query";

export const COMMENT_PATHS = Object.freeze({
  GET_ACTION_ITEM_NOTES: (actionItemId: string) =>
    `/action-items/${actionItemId}/comments`,
  CREATE_OR_UPDATE_ACTION_ITEM_NOTES: "/action-items/comments/create-or-update",
   DELETE_ACTION_ITEM_NOTES : (noteId: string) => `/action-items/comments/${noteId}/delete`,
});

interface CreateOrUpdateNoteParams {
  comment: string;
  commentType: string;
  isForUpdate: boolean;
  commentId?: string;
  actionItemId: string;
  mentionedPorIds?: string[];
}

// GET
const getActionItemNotes = async (actionItemId: string, sortOrder: string ): Promise<any> => {
  const response = await axiosInstance.get(COMMENT_PATHS.GET_ACTION_ITEM_NOTES(actionItemId),
    { params: { sortOrder } }
);
  return response;
};

const useGetActionItemNote = (actionItemId: string, enabled: boolean, sortOrder: string) => {
  return useQuery({
    queryKey: ['get_action_item_comments', actionItemId, sortOrder],
    queryFn: () => getActionItemNotes(actionItemId, sortOrder),
    enabled,
    retry: 0,
  });
};

// CREATE
const createActionItemNote = async (actionItemId: string, params: CreateOrUpdateNoteParams) => {
  const response = await axiosInstance.post(COMMENT_PATHS.CREATE_OR_UPDATE_ACTION_ITEM_NOTES, {
    ...params,
    commentId: undefined,
    actionItemId,
  });
  return response;
};

const useCreateActionItemNote = (actionItemId: string, handlerFunctions: any) => {
  return useMutation<any, any, CreateOrUpdateNoteParams>({
    mutationKey: ['create_action_item_note', actionItemId],
    mutationFn: (params) => createActionItemNote(actionItemId, params).then((res) => res),
    ...handlerFunctions,
  });
};

// UPDATE
const updateActionItemNote = async (actionItemId: string, params: CreateOrUpdateNoteParams) => {
  const response = await axiosInstance.post(COMMENT_PATHS.CREATE_OR_UPDATE_ACTION_ITEM_NOTES, {
    ...params,
    actionItemId,
  });
  return response;
};

const useUpdateActionItemNote = (actionItemId: string, handlerFunctions: any) => {
  return useMutation<any, any, CreateOrUpdateNoteParams>({
    mutationKey: ['update_action_item_note', actionItemId],
    mutationFn: (params) => updateActionItemNote(actionItemId, params).then((res) => res),
    ...handlerFunctions,
  });
};

const deleteActionItemNote = async (noteId: string) => {
    const response = await axiosInstance.delete(COMMENT_PATHS.DELETE_ACTION_ITEM_NOTES(noteId));
    return response;
  };

  const useDeleteActionItemNote = (noteId: string, handlerFunctions: any) => {
    return useMutation<any, any, { action_item_note_id: string }>({
      mutationKey: ['delete_action_item_note', noteId],
      mutationFn: () => deleteActionItemNote(noteId).then((res) => res),
      ...handlerFunctions,
    });
  };
  

export interface ActionItemNotesService {
  readonly useGetActionItemNote: (actionItemId: string, enabled: boolean, sortOrder: string,) => any;
  readonly useCreateActionItemNote: (actionItemId: string, handlerFunctions: any) => any;
  readonly useUpdateActionItemNote: (actionItemId: string, handlerFunctions: any) => any;
  readonly useDeleteActionItemNote: (noteId: string, handlerFunctions: any) => any;
}

const ActionItemNotesService: ActionItemNotesService = Object.freeze({
  useGetActionItemNote,
  useCreateActionItemNote,
  useUpdateActionItemNote,
  useDeleteActionItemNote,
});

export default ActionItemNotesService;
